/**
 * Reduce an API error to the fields the application actually reads.
 *
 * Why this exists (#1741): Volto's api middleware puts the rejection value of
 * an API call straight into the Redux store, and `Html.jsx` serialises that
 * store into `window.__data`. A superagent error carries its own request
 * object, and `superagent`'s `RequestBase.toJSON()` renders that request's
 * outgoing headers — including the visitor's `x-forwarded-for`. Any page whose
 * SSR render hit a failing API call therefore shipped one visitor's IP address
 * in its HTML, and Varnish served that HTML to every other visitor.
 *
 * Allow-listing beats blocking the known-bad keys: a future superagent or Volto
 * version can add another field carrying request state, and an allow-list keeps
 * it out of the store without anyone having to notice.
 *
 * A key that is absent here reads as `undefined` on the result, which is what
 * the consumers already expect — `View.jsx` treats `error.status === undefined`
 * as the CORS case, so a status must never be normalised to `null`.
 */

/** Fields read from the error itself. */
const ERROR_KEYS = [
  'code', // View.jsx: [301, 302].includes(error.code); 'ECONNREFUSED'
  'status', // View.jsx / Error.tsx: views.errorViews[error.status.toString()]
  'message', // Add.jsx: errorsList.error?.message
  'url', // View.jsx: flattenToAppURL(error.url) on a redirect
  'crossDomain', // api.js: branches into the CORSERROR api error
] as const;

/** Fields read from `error.response`. */
const RESPONSE_KEYS = [
  'statusCode', // api.js: branches for 504 and 401
  'status',
  'body', // Add.jsx / Contents.jsx: error.response.body.message
  'text', // Add.jsx: error.response.text
] as const;

/**
 * Fields kept from `error.response.error`.
 *
 * That field is superagent's `Response.toError()` (`node/response.js:88`), and
 * two reducers put it into the store: `userSession.js:60` for `LOGIN_FAIL` —
 * note it does *not* optional-chain — and `workingcopy.js:96`. Its consumers
 * only test it for truthiness: `Login.jsx:109` and `LoginOIDC.jsx:58` show a
 * toast whose text comes from `intl`, not from the error. Dropping the field
 * would leave `userSession.login.error` undefined, so a failed login or OIDC
 * callback would show neither message nor redirect.
 *
 * It must not survive raw, though: `toError()` also sets `message` to
 * `cannot ${method} ${path} (${status})`, plus `text`, `method` and `path` —
 * the internal backend path. `status` is enough to keep the object truthy and
 * useful.
 */
const RESPONSE_ERROR_KEYS = ['status'] as const;

export interface SanitizedApiResponse {
  statusCode?: number;
  status?: number;
  body?: unknown;
  text?: string;
  error?: { status?: number };
}

export interface SanitizedApiError {
  code?: number | string;
  status?: number;
  message?: string;
  url?: string;
  crossDomain?: boolean;
  response?: SanitizedApiResponse;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Copy the allow-listed keys, skipping the ones that carry no value so an
 * absent key stays absent rather than becoming an explicit `undefined`.
 */
const pick = (
  source: Record<string, unknown>,
  keys: readonly string[],
): Record<string, unknown> => {
  const picked: Record<string, unknown> = {};
  keys.forEach((key) => {
    if (source[key] !== undefined) {
      picked[key] = source[key];
    }
  });
  return picked;
};

/**
 * A Node system error — ECONNREFUSED, ENOTFOUND, ETIMEDOUT — names the address
 * it failed to reach in its own message: `connect ECONNREFUSED 127.0.0.1:5100`.
 * That address is the internal backend, so for these the message goes too.
 *
 * Nothing displays it: `App.jsx:176` renders `ConnectionRefusedView`, which
 * takes no message, and the middleware branches on `code`, which survives.
 * `message` stays for HTTP errors, where superagent uses the status text
 * ("Not Found") and `Add.jsx` and the forms show it to the user.
 */
const isSystemError = (error: Record<string, unknown>): boolean =>
  typeof error.syscall === 'string';

/**
 * @param error The rejection value of an API call — a superagent error, one of
 *   the plain objects thrown by Volto's `helpers/Api`, or anything else.
 * @returns A plain object holding only the allow-listed fields. Non-object
 *   input (`null`, `undefined`, a string) is returned unchanged.
 */
export function sanitizeApiError(error: unknown): unknown {
  if (!isObject(error)) {
    return error;
  }

  const errorKeys = isSystemError(error)
    ? ERROR_KEYS.filter((key) => key !== 'message')
    : ERROR_KEYS;
  const safe = pick(error, errorKeys) as SanitizedApiError;

  if (isObject(error.response)) {
    const response = pick(
      error.response,
      RESPONSE_KEYS,
    ) as SanitizedApiResponse;

    // superagent sets `response.error` to `false` on a successful response, so
    // guard on the object: turning `false` into `{}` would make it truthy and
    // fire the login-failed toast on a perfectly good login.
    if (isObject(error.response.error)) {
      response.error = pick(error.response.error, RESPONSE_ERROR_KEYS);
    }

    safe.response = response;
  }

  return safe;
}
